/**
 * ClusteredMarkers Component
 *
 * A component that renders markers with clustering support for react-leaflet v5.
 * This component bypasses react-leaflet's Marker component and directly uses Leaflet API
 * for better performance with large datasets.
 *
 * @component
 */

import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

/**
 * ClusteredMarkers Component
 *
 * @param {Object} props - Component props
 * @param {Array} props.markers - Array of marker data
 * @param {boolean} props.enabled - Whether clustering is enabled
 * @param {Object} props.clusterOptions - Custom cluster options
 * @returns {null} This component doesn't render anything directly
 */
const ClusteredMarkers = ({
  markers = [],
  enabled = true,
  clusterOptions = {},
}) => {
  const map = useMap();
  const clusterGroupRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!map) return;

    // Default cluster options
    const defaultOptions = {
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true, // Keep as boolean, not object
      spiderfyOnMaxZoom: true,
      spiderfyOnEveryZoom: false,
      removeOutsideVisibleBounds: true,
      animate: true,
      animateAddingMarkers: false,
      maxClusterRadius: 80,
      disableClusteringAtZoom: 18,
      chunkedLoading: true,
      // Debug logging
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const childMarkers = cluster.getAllChildMarkers();

        // Debug log untuk melihat jumlah sebenarnya
        console.log(`Cluster created with ${count} markers:`, {
          childMarkers: childMarkers.length,
          reportedCount: count,
          positions: childMarkers.map((m) => m.getLatLng()),
        });

        let sizeClass = "small";
        let size = 35;

        if (count >= 100) {
          sizeClass = "large";
          size = 50;
        } else if (count >= 10) {
          sizeClass = "medium";
          size = 42;
        }

        return new L.DivIcon({
          html: `<div class="cluster-inner"><span>${count}</span></div>`,
          className: `marker-cluster marker-cluster-${sizeClass}`,
          iconSize: new L.Point(size, size),
        });
      },
    };

    const options = { ...defaultOptions, ...clusterOptions };

    // Clear existing markers and cluster group first
    if (clusterGroupRef.current && map.hasLayer(clusterGroupRef.current)) {
      map.removeLayer(clusterGroupRef.current);
      clusterGroupRef.current.clearLayers(); // Clear all markers from cluster
      clusterGroupRef.current = null;
    }

    // Clear individual markers
    markersRef.current.forEach((marker) => {
      if (map.hasLayer(marker)) {
        map.removeLayer(marker);
      }
    });
    markersRef.current = [];

    // Additional cleanup: remove any orphaned markers
    map.eachLayer((layer) => {
      if (
        layer instanceof L.Marker &&
        !(layer instanceof L.MarkerClusterGroup)
      ) {
        // Check if this marker is not part of any cluster group
        const isOrphaned = !markers.some(
          (markerData) =>
            markerData.position &&
            Math.abs(layer.getLatLng().lat - markerData.position[0]) < 0.0001 &&
            Math.abs(layer.getLatLng().lng - markerData.position[1]) < 0.0001
        );
        if (isOrphaned) {
          map.removeLayer(layer);
        }
      }
    });

    if (markers.length === 0) return;

    if (enabled) {
      // Create cluster group
      clusterGroupRef.current = L.markerClusterGroup(options);

      // Filter out duplicate positions and add markers to cluster
      const uniqueMarkers = [];
      const seenPositions = new Set();

      markers.forEach((markerData, index) => {
        if (!markerData.position || !Array.isArray(markerData.position)) return;

        // Create unique key for position
        const positionKey = `${markerData.position[0].toFixed(
          6
        )}_${markerData.position[1].toFixed(6)}`;

        if (seenPositions.has(positionKey)) {
          console.warn(
            `Duplicate marker position detected at index ${index}:`,
            markerData.position
          );
          return; // Skip duplicate
        }

        seenPositions.add(positionKey);
        uniqueMarkers.push(markerData);
      });

      console.log(
        `Adding ${uniqueMarkers.length} unique markers to cluster (filtered from ${markers.length} total)`
      );

      // Add unique markers to cluster
      uniqueMarkers.forEach((markerData) => {
        const marker = L.marker(markerData.position, {
          icon: markerData.icon,
        });

        // Add popup with smooth options
        if (markerData.popup) {
          marker.bindPopup(markerData.popup, {
            closeButton: false,
            autoPan: true,
            keepInView: true,
            className: "custom-popup",
            offset: [0, -10],
            maxWidth: 300,
            minWidth: 200,
          });
        }

        // Add event handlers
        if (markerData.eventHandlers) {
          Object.entries(markerData.eventHandlers).forEach(
            ([event, handler]) => {
              marker.on(event, (e) => {
                handler(e, marker);
              });
            }
          );
        }

        clusterGroupRef.current.addLayer(marker);
        markersRef.current.push(marker);
      });

      // Add cluster to map
      map.addLayer(clusterGroupRef.current);
    } else {
      // Add markers directly without clustering (also filter duplicates)
      const uniqueMarkers = [];
      const seenPositions = new Set();

      markers.forEach((markerData, index) => {
        if (!markerData.position || !Array.isArray(markerData.position)) return;

        const positionKey = `${markerData.position[0].toFixed(
          6
        )}_${markerData.position[1].toFixed(6)}`;

        if (seenPositions.has(positionKey)) {
          console.warn(
            `Duplicate marker position detected at index ${index}:`,
            markerData.position
          );
          return;
        }

        seenPositions.add(positionKey);
        uniqueMarkers.push(markerData);
      });

      uniqueMarkers.forEach((markerData) => {
        const marker = L.marker(markerData.position, {
          icon: markerData.icon,
        });

        if (markerData.popup) {
          marker.bindPopup(markerData.popup, {
            closeButton: false,
            autoPan: true,
            keepInView: true,
            className: "custom-popup",
            offset: [0, -10],
            maxWidth: 300,
            minWidth: 200,
          });
        }

        if (markerData.eventHandlers) {
          Object.entries(markerData.eventHandlers).forEach(
            ([event, handler]) => {
              marker.on(event, (e) => {
                handler(e, marker);
              });
            }
          );
        }

        marker.addTo(map);
        markersRef.current.push(marker);
      });
    }

    // Cleanup
    return () => {
      markersRef.current.forEach((marker) => {
        if (map && map.hasLayer(marker)) {
          map.removeLayer(marker);
        }
      });
      markersRef.current = [];

      if (
        clusterGroupRef.current &&
        map &&
        map.hasLayer(clusterGroupRef.current)
      ) {
        map.removeLayer(clusterGroupRef.current);
      }
    };
  }, [map, markers, enabled, clusterOptions]);

  return null;
};

export default ClusteredMarkers;
